import { Injectable, Injector } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { ClientLoggerService } from './client-logger.service';
import { LogCategory } from './client-log.models';

/**
 * Registriert globale window-Listener für Fehler, die NICHT durch Angulars
 * Zone/ErrorHandler laufen und deshalb sonst nie im Seq-Log landen:
 *
 *  - `unhandledrejection`: nicht gefangene Promise-Rejections. Auf Capacitor
 *    der häufigste Fall (z. B. ein `await SomePlugin.foo()`, das rejected,
 *    ohne `.catch()` — native Plugin-/Bridge-Fehler tauchen hier auf).
 *  - `error` (window.onerror): nicht gefangene Script-Fehler und Fehler aus
 *    Callbacks außerhalb der Angular-Zone (native Plugin-Callbacks etc.).
 *
 * Der Angular `GlobalErrorHandler` deckt nur Fehler ab, die durch die Zone
 * laufen — diese Listener schließen die Lücke. Echte native Crashes
 * (Java/Kotlin-, Swift-Exceptions, WebView-RenderProcessGone) erreichen die
 * JS-Schicht gar nicht und sind hier bewusst NICHT abgedeckt.
 *
 * HERKUNFT (Seq-Filterung): jedes Log trägt `platform` (web|android|ios) und
 * `isNative` in den Properties — so ist im Backend sofort sichtbar, ob der
 * Fehler aus der nativen App (Android/iOS) oder aus dem Web stammt. Der aus dem
 * UserAgent abgeleitete `Os`-Wert allein kann das nicht unterscheiden.
 *
 * ANTI-SPAM: bekanntes Browser-Rauschen (Cross-Origin „Script error.",
 * ResizeObserver-Loop, abgebrochene Requests) wird verworfen; alles Weitere
 * begrenzt zusätzlich der LogDeduplicationService (max. 3× gleiche Meldung/30s).
 */
@Injectable({
  providedIn: 'root',
})
export class GlobalErrorListenersService {
  private isInitialized = false;

  /**
   * Meldungs-Fragmente, die als bekanntes, nicht handlungsrelevantes Rauschen
   * gelten und gar nicht erst geloggt werden. Bewusst kurz gehalten —
   * lieber wenige, sichere Einträge als echte Fehler zu verschlucken.
   */
  private readonly NOISE_PATTERNS: string[] = [
    'script error.', // Cross-Origin-Script ohne Details — nicht auswertbar
    'resizeobserver loop', // bekanntes, harmloses Browser-Layout-Rauschen
  ];

  /** Error-Namen, die typischerweise von abgebrochenen Aktionen stammen. */
  private readonly NOISE_ERROR_NAMES: string[] = ['AbortError'];

  constructor(private injector: Injector) {}

  initialize(): void {
    if (this.isInitialized || typeof window === 'undefined') {
      return;
    }

    window.addEventListener('unhandledrejection', this.onUnhandledRejection);
    window.addEventListener('error', this.onWindowError, true);

    this.isInitialized = true;
    console.log(
      '[GlobalErrorListeners] Initialized - unhandledrejection & window.onerror werden jetzt geloggt'
    );
  }

  /** Nicht gefangene Promise-Rejection → Log. */
  private onUnhandledRejection = (event: PromiseRejectionEvent): void => {
    const reason = event?.reason;
    const error =
      reason instanceof Error
        ? reason
        : new Error(this.stringifyReason(reason));

    this.log(error, 'unhandledrejection', { reasonType: typeof reason });
  };

  /**
   * Nicht gefangener Script-Fehler → Log.
   * Ressourcen-Ladefehler (img/script/link schlägt fehl) feuern dasselbe
   * Event mit einem Element als `target` und ohne `error`/`message` — die
   * werden hier herausgefiltert, da sie keine JS-Exceptions sind.
   */
  private onWindowError = (event: ErrorEvent | Event): void => {
    const errorEvent = event as ErrorEvent;
    const isResourceError =
      !!event.target && event.target !== window && !errorEvent.error && !errorEvent.message;
    if (isResourceError) {
      return;
    }

    const error =
      errorEvent.error instanceof Error
        ? errorEvent.error
        : new Error(errorEvent.message || 'Unknown window error');

    this.log(error, 'window.onerror', {
      filename: errorEvent.filename,
      lineno: errorEvent.lineno,
      colno: errorEvent.colno,
    });
  };

  private log(
    error: Error,
    source: 'unhandledrejection' | 'window.onerror',
    extra: { [key: string]: any }
  ): void {
    if (this.isNoise(error)) {
      return;
    }

    try {
      // Lazy-Lookup um zirkuläre Abhängigkeiten zu vermeiden (wie im
      // GlobalErrorHandler / ConsoleErrorInterceptor).
      const logger = this.injector.get(ClientLoggerService);
      logger.logError(error, {
        category: LogCategory.Error,
        componentName: 'GlobalErrorListeners',
        properties: {
          source,
          // HERKUNFT: erlaubt im Backend/Seq die Filterung android|ios|web
          // und native-App vs. Web — unabhängig vom UA-abgeleiteten Os.
          platform: Capacitor.getPlatform(),
          isNative: Capacitor.isNativePlatform(),
          ...extra,
        },
      });
    } catch {
      // Beim Logging selbst nicht erneut Fehler werfen.
    }
  }

  /** Verwirft bekanntes Browser-Rauschen (siehe NOISE_PATTERNS / -NAMES). */
  private isNoise(error: Error): boolean {
    if (this.NOISE_ERROR_NAMES.includes(error.name)) {
      return true;
    }
    const message = (error.message || '').toLowerCase();
    return this.NOISE_PATTERNS.some((pattern) => message.includes(pattern));
  }

  /** Macht aus einem beliebigen Rejection-Grund eine lesbare Message. */
  private stringifyReason(reason: any): string {
    if (reason === null || reason === undefined) {
      return `Unhandled promise rejection: ${String(reason)}`;
    }
    if (typeof reason === 'string') {
      return reason;
    }
    try {
      return `Unhandled promise rejection: ${JSON.stringify(reason)}`;
    } catch {
      return `Unhandled promise rejection: ${String(reason)}`;
    }
  }
}
